<script setup lang="ts">
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
import { useHead } from '@vueuse/head';
import { onBeforeMount } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import Container from '@/components/base/layout/Container.vue';
import ThemedButton from '@/components/base/ThemedButton.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import CustomizerRecommendations from '@/components/customizer/CustomizerRecommendations.vue';
import { gtag } from '@/utils/analytics';
import { scrollToTarget } from '@/utils/scrollTo';

const route = useRoute();
const router = useRouter();

const heroImage = nutshell['static_img/custom-builder/custom_trail_mix_hero.jpeg'];

const handleBuildYourOwn = () => {
  gtag('event', 'build_your_own_mix');
};

const isMobile = useBreakpoints(breakpointsTailwind).smaller('md');

onBeforeMount(() => {
  if (route.hash) {
    router.replace(`/custom/mixes/builder?code=${route.hash.replace('#', '')}`);
  }
});

useHead({
  meta: [
    {
      content: `Build your own custom mix of delicious snacks! Customize with 150+ different
          options of nuts, dried fruit, chocolates and more.`,
      key: 'og:description',
      property: 'og:description',
    },
    { property: 'og:image', content: heroImage, key: 'og:image' },
    { property: 'og:title', content: 'Build Your Own Mix | Nuts.com', key: 'og:title' },
    { property: 'og:url', content: 'https://nuts.com/custom/mixes', key: 'og:url' },
  ],
  title: 'Build Your Own Mix | Nuts.com',
});
</script>

<template>
  <div>
    <Container class="my-6 lg:mb-20">
      <h1 class="font-semibold" data-test="build-your-own-mix-title">Custom Trail Mixes</h1>

      <div class="flex flex-col lg:flex-row">
        <img
          :src="heroImage"
          alt="Custom mix Hero Image"
          class="w-full rounded-2xl lg:w-2/3"
          data-test="custom-mix-hero-image"
        />
        <div class="flex flex-col w-full lg:ml-8 lg:w-1/3" data-test="custom-builder-summary">
          <div class="w-full my-4 lg:mt-0">
            <RouterLink
              to="/custom/mixes/builder"
              class="w-full hover:no-underline"
              @click="handleBuildYourOwn"
            >
              <ThemedButton
                class="mb-4"
                fullWidth
                tabindex="-1"
                theme="green"
                data-test="build-your-box-button"
              >
                Build Your Own
              </ThemedButton>
            </RouterLink>
            <ThemedButton
              theme="white"
              data-test="see-recommendations-button"
              fullWidth
              @click="scrollToTarget('#recommendations', isMobile ? 15 : -110)"
            >
              See Recommendations
            </ThemedButton>
          </div>
          <BaseBodyText>
            Build your own trail mix! Customize with 200+ different options of nuts, dried fruit,
            chocolates and more.
          </BaseBodyText>
          <ul class="pl-5 list-disc">
            <li class="mb-2.5">
              <BaseBodyText>
                Choose everything you want, and nothing you don't. The possibilities are endless!
              </BaseBodyText>
            </li>
            <li class="mb-2.5">
              <BaseBodyText>
                Not sure where to start? Check out our recommended combinations for something that’s
                sure to be delicious!
              </BaseBodyText>
            </li>
          </ul>
        </div>
      </div>
    </Container>

    <div class="flex justify-center w-full py-16 bg-nuts-stone-100">
      <Container>
        <CustomizerRecommendations id="recommendations" data-test="recommendation-section" />
      </Container>
    </div>
  </div>
</template>
